import React, { useState, useMemo, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Tabs,
  Text,
  Badge,
  Button,
  StatsGrid,
  StatusDot,
  VersionTimeline,
  Modal,
  Input,
  SearchInput,
} from '@teals/ui-web';
import type { TabItem, VersionEntry, StatItem } from '@teals/ui-web';
import {
  useGetAgentQuery,
  useGetAgentsQuery,
  usePublishAgentMutation,
  useCreateAgentVersionMutation,
  useSendMessageMutation,
  useGetSessionHistoryQuery,
  useGetOrgModelsQuery,
  type Agent,
  type AgentDetail as AgentDetailType,
} from '../../store/api';
import ScopedAnalyticsPanel from '../Analytics/ScopedAnalyticsPanel';
import SessionsTab from './SessionsTab';
import ConfigureTab from './ConfigureTab';
import {
  AgentSystemSummary,
  PartOfSystemCard,
  HintChips,
  MembershipChip,
  CapabilitiesRow,
  typeBadgeLabel,
  isManagementAgent,
} from './AgentSystemPanels';
import { useWebSocketContext } from '../../providers/WebSocketProvider';
import type { ExchangeEvent } from '../../hooks/useWebSocket';
import {
  formatModelOptionLabel,
  groupModelsByFamily,
  matchesModelSearch,
} from '../../utils/modelDisplay';
import type { OrgModel } from '../../store/api/orgModelsApi';
import { useAuth } from '../../hooks/useAuth';

type DetailTab = 'overview' | 'configure' | 'sessions' | 'analytics' | 'playground';

interface AgentConfig {
  systemPrompt: string;
  model: string;
  temperature: number;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

const TABS: TabItem[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'configure', label: 'Configure' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'playground', label: 'Playground' },
];

const VALID_TABS: DetailTab[] = [
  'overview',
  'configure',
  'sessions',
  'analytics',
  'playground',
];

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;

  @media (max-width: 767px) {
    padding: 1.5rem 1rem;
  }
`;

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  margin-bottom: 0.25rem;

  &:hover {
    color: var(--accent);
  }
`;

const AgentHeader = styled.div`
  padding-bottom: 1.25rem;
  border-bottom: 1px solid var(--border);
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
  flex: 1;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const TabContent = styled.div`
  margin-top: 0.5rem;
`;

const OverviewPanels = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: start;

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
  }
`;

const OverviewPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SectionLabel = styled.h3`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 0.75rem;
`;

const MetaSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const MetaList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const MetaItem = styled.li`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border);
  font-size: 0.875rem;

  &:last-child {
    border-bottom: none;
  }
`;

const MetaKey = styled.span`
  color: var(--text-muted);
`;

const MetaValue = styled.span`
  color: var(--text-primary);
  font-weight: 500;
  text-align: right;
`;

const PartnerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border);
  font-size: 0.875rem;

  &:last-child {
    border-bottom: none;
  }
`;

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 500px;
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  overflow: hidden;
  background: var(--bg-surface);

  @media (max-width: 1023px) {
    height: min(500px, 55dvh);
  }
`;

const JsonPre = styled.pre`
  margin: 0;
  font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', monospace;
  font-size: 0.8125rem;
  color: var(--text-primary);
  line-height: 1.6;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

const PlaygroundLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  align-items: stretch;

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
  }
`;

const InspectorPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  padding: 1rem;
  background: var(--bg-surface);
  min-height: 500px;
  max-height: 500px;
  overflow: auto;

  @media (max-width: 1023px) {
    min-height: min(500px, 50dvh);
    max-height: none;
  }
`;

const InspectorSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const InspectorTitle = styled.h5`
  margin: 0;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-secondary);
`;

const EventLog = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-family: 'SF Mono', 'Fira Code', monospace;
  color: var(--text-secondary);
`;

const PendingBubble = styled.div`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.75rem;
  background: var(--bg-elevated, rgba(148, 163, 184, 0.12));
  color: var(--text-secondary);
  font-size: 0.8125rem;
`;

const PendingDots = styled.span`
  display: inline-flex;
  gap: 0.25rem;

  span {
    width: 0.375rem;
    height: 0.375rem;
    border-radius: 50%;
    background: var(--accent, #14b8a6);
    animation: playgroundPendingPulse 1.2s ease-in-out infinite;
  }

  span:nth-child(2) {
    animation-delay: 0.2s;
  }

  span:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes playgroundPendingPulse {
    0%,
    80%,
    100% {
      opacity: 0.35;
      transform: translateY(0);
    }
    40% {
      opacity: 1;
      transform: translateY(-2px);
    }
  }
`;

const SystemLine = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const ChatBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: var(--bg-elevated, #f8fafc);
  border-bottom: 1px solid var(--border);
  font-size: 0.8125rem;
  color: var(--text-secondary);
  flex-wrap: wrap;
`;

const ModelSelect = styled.select`
  font-size: 0.75rem;
  font-family: inherit;
  color: var(--text-primary);
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 0.25rem 0.5rem;
  max-width: 14rem;
`;

const ModelPickerTrigger = styled.button`
  font-size: 0.75rem;
  font-family: inherit;
  color: var(--text-primary);
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 0.25rem 0.625rem;
  max-width: 16rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    border-color: var(--accent, #2d9a8f);
  }

  &:focus-visible {
    outline: 2px solid var(--accent, #2d9a8f);
    outline-offset: 1px;
  }
`;

const ModelPickerBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: min(100%, 22rem);
`;

const ModelPickerList = styled.div`
  max-height: 20rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ModelFamilyLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  padding: 0.25rem 0.25rem 0.125rem;
`;

const ModelOptionButton = styled.button<{ $active?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  font-size: 0.8125rem;
  font-family: inherit;
  padding: 0.5rem 0.625rem;
  border-radius: 6px;
  border: 1px solid
    ${({ $active }) => ($active ? 'var(--accent, #2d9a8f)' : 'transparent')};
  background: ${({ $active }) =>
    $active ? 'var(--accent-subtle, rgba(45, 154, 143, 0.12))' : 'transparent'};
  color: var(--text-primary);
  cursor: pointer;

  &:hover {
    background: var(--bg-elevated, #f8fafc);
  }
`;

const MetaChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.5rem;
`;

const SoftChip = styled.button`
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--bg-elevated, #f8fafc);
  color: var(--text-secondary);
  font-size: 0.75rem;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

const MessageList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const MessageBubble = styled.div<{ $role: 'user' | 'assistant' }>`
  max-width: 75%;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  line-height: 1.5;
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  background: ${({ $role }) =>
    $role === 'user' ? 'var(--accent)' : 'var(--bg-elevated, #f1f5f9)'};
  color: ${({ $role }) => ($role === 'user' ? 'white' : 'var(--text-primary)')};
`;

const ChatInputRow = styled.form`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--border);
`;

const ChatInput = styled.input`
  flex: 1;
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-primary);
  background: transparent;
  outline: none;

  &:focus {
    border-color: var(--accent);
  }
`;

const PageMessage = styled.div`
  padding: 3rem 1rem;
  text-align: center;
  color: var(--text-muted);
`;

const ErrorBanner = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: color-mix(in srgb, var(--danger, #dc2626) 12%, transparent);
  color: var(--danger, #dc2626);
  font-size: 0.875rem;
`;

const ModalForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const ModalCopy = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--text-secondary);

  code {
    font-size: 0.8125rem;
  }
`;

const SCOPE_BADGES: Record<
  string,
  { variant: 'primary' | 'info' | 'warning' | 'neutral'; label: string }
> = {
  global: { variant: 'primary', label: 'Global' },
  organisation: { variant: 'info', label: 'Organisation' },
  application: { variant: 'warning', label: 'Application' },
  system: { variant: 'neutral', label: 'System' },
};

function stripVersionPrefix(label: string): string {
  return label.replace(/^v/i, '').trim();
}

function formatVersionLabel(label: string | null | undefined): string {
  if (!label || label === '—') return '';
  const bare = stripVersionPrefix(label);
  return bare ? `v${bare}` : '';
}

/** Suggest next minor semver from published_version or version history. */
function suggestNextVersion(agent: AgentDetailType): string {
  const published =
    agent.publishedVersion ||
    agent.versions.find((v) => v.status === 'published' && v.isLatest)?.version ||
    agent.versions.find((v) => v.status === 'published')?.version ||
    (agent.versionStatus === 'published' ? agent.version : undefined) ||
    agent.versions[0]?.version ||
    '1.0';
  const bare = stripVersionPrefix(String(published));
  const parts = bare.split('.');
  if (parts.length >= 2) {
    const major = parts[0] || '1';
    const minor = Number.parseInt(parts[1] || '0', 10);
    const nextMinor = Number.isFinite(minor) ? minor + 1 : 1;
    return `${major}.${nextMinor}`;
  }
  const major = Number.parseInt(parts[0] || '1', 10);
  if (Number.isFinite(major)) return `${major}.1`;
  return '1.1';
}

function canPublishAgent(agent: Agent): boolean {
  return (
    agent.versionStatus === 'draft' ||
    agent.versionStatus === 'sandbox' ||
    Boolean(agent.hasOpenDraft)
  );
}

function versionStatusBadgeText(agent: Agent): string {
  if (agent.versionStatus === 'draft' || (agent.hasOpenDraft && agent.draftVersion)) {
    const label =
      formatVersionLabel(agent.draftVersion || agent.version) || agent.version;
    return `Draft ${label}`.replace(/\s+/g, ' ').trim();
  }
  if (
    agent.versionStatus === 'sandbox' ||
    (agent.hasOpenDraft && agent.sandboxVersion)
  ) {
    const label =
      formatVersionLabel(agent.sandboxVersion || agent.version) || agent.version;
    return `Sandbox ${label}`.replace(/\s+/g, ' ').trim();
  }
  const pub =
    formatVersionLabel(agent.publishedVersion || agent.version) || agent.version;
  const showLatest =
    agent.isLatest === true ||
    (agent.versionStatus === 'published' && !agent.hasOpenDraft);
  if (showLatest && pub) {
    return `Published ${pub} · latest`;
  }
  return pub ? `Published ${pub}` : 'Published';
}

function playgroundAgentVersion(agent: Agent): string | undefined {
  if (
    agent.versionStatus === 'draft' ||
    agent.versionStatus === 'sandbox' ||
    agent.hasOpenDraft
  ) {
    const raw =
      agent.draftVersion ||
      agent.sandboxVersion ||
      (agent.version !== '—' ? agent.version : undefined);
    return raw || undefined;
  }
  return undefined;
}

/** Pull a human message from nested portal/API error payloads (RTK + Underpin). */
function extractPortalChatError(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null;
  const data = (err as { data?: unknown }).data;
  const candidates: unknown[] = [data];
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    candidates.push(d.message, d['error-details'], d.detail);
  }
  for (const c of candidates) {
    if (typeof c !== 'string' || !c.trim()) continue;
    let text = c.trim();
    // Nested JSON strings from Underpin wrappers
    for (let i = 0; i < 3; i += 1) {
      if (!text.startsWith('{')) break;
      try {
        const parsed = JSON.parse(text) as Record<string, unknown>;
        const next =
          (typeof parsed.message === 'string' && parsed.message) ||
          (typeof parsed['error-details'] === 'string' &&
            parsed['error-details']) ||
          null;
        if (!next) break;
        text = next;
      } catch {
        break;
      }
    }
    // Prefer the actionable Bedrock / LLM clause when present
    const bedrockIdx = text.indexOf('Bedrock Converse failed:');
    if (bedrockIdx >= 0) {
      return text.slice(bedrockIdx).replace(/^Error::\s*/i, '').trim();
    }
    const cleaned = text.replace(/^Error::\s*/i, '').trim();
    if (cleaned && cleaned !== 'NA') return cleaned;
  }
  if (err instanceof Error && err.message) return err.message;
  return null;
}

const AgentDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as DetailTab) || 'overview';
  const deepLinkSessionId = searchParams.get('sessionId') || undefined;
  const [activeTab, setActiveTab] = useState<DetailTab>(
    VALID_TABS.includes(initialTab) ? initialTab : 'overview',
  );
  const [config, setConfig] = useState<AgentConfig>({
    systemPrompt: '',
    model: 'gpt-4o',
    temperature: 0.7,
  });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [sessionId, setSessionId] = useState<string | undefined>(deepLinkSessionId);
  const [hydratedSessionId, setHydratedSessionId] = useState<string | undefined>();
  const [actionError, setActionError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [createVersionOpen, setCreateVersionOpen] = useState(false);
  const [newVersion, setNewVersion] = useState('');
  const [newVersionNote, setNewVersionNote] = useState('');
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [wsEvents, setWsEvents] = useState<ExchangeEvent[]>([]);
  const [llmLog, setLlmLog] = useState<string[]>([]);
  const [awaitingReply, setAwaitingReply] = useState(false);
  const [modelOverride, setModelOverride] = useState('');
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  const [playgroundModelSearch, setPlaygroundModelSearch] = useState('');
  const [effortOverride, setEffortOverride] = useState<'' | 'low' | 'medium' | 'high'>('');
  const [resolvedModel, setResolvedModel] = useState<string | null>(null);
  const [resolvedEffort, setResolvedEffort] = useState<string | null>(null);
  const [resolvedVersion, setResolvedVersion] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: agent, isLoading, isError } = useGetAgentQuery(id, {
    skip: !id || id === 'new',
  });
  const { data: catalogAgents = [] } = useGetAgentsQuery();
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const { data: orgModels } = useGetOrgModelsQuery(orgId!, { skip: !orgId });
  const [publishAgent] = usePublishAgentMutation();
  const [createAgentVersion] = useCreateAgentVersionMutation();
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();
  const showPending = isSending || awaitingReply;
  const {
    subscribe,
    unsubscribe,
    addListener,
    removeListener,
    isConnected,
    connectionState,
  } = useWebSocketContext();

  const { data: playgroundHistory } = useGetSessionHistoryQuery(
    deepLinkSessionId || sessionId || '',
    {
      skip:
        activeTab !== 'playground' ||
        !(deepLinkSessionId || sessionId) ||
        hydratedSessionId === (deepLinkSessionId || sessionId),
    },
  );

  const readOnly = Boolean(
    agent?.readOnly || agent?.scope === 'global' || agent?.scope === 'system',
  );

  /** Default | Auto | recommended ∪ org-enabled chat product ids (exclude agent default). */
  const playgroundModelCatalog = useMemo(() => {
    const defaultModel = agent?.llmModel || agent?.config?.model || '';
    const recommended =
      agent?.recommendedModels || agent?.config?.recommendedModels || [];
    const byId = new Map<string, OrgModel>();
    for (const m of orgModels || []) {
      if (!m.enabled || m.chatCapable === false) continue;
      byId.set(m.productId, m);
    }
    const seen = new Set<string>();
    const models: OrgModel[] = [];
    for (const id of [...recommended, ...byId.keys()]) {
      const pid = String(id || '').trim();
      if (!pid || pid === defaultModel || seen.has(pid)) continue;
      seen.add(pid);
      const known = byId.get(pid);
      models.push(
        known || {
          productId: pid,
          displayName: pid,
          provider: '',
          capabilities: [],
          enabled: true,
          chatCapable: true,
        },
      );
    }
    return models;
  }, [
    agent?.llmModel,
    agent?.config?.model,
    agent?.recommendedModels,
    agent?.config?.recommendedModels,
    orgModels,
  ]);

  const playgroundModelGroups = useMemo(() => {
    const filtered = playgroundModelSearch.trim()
      ? playgroundModelCatalog.filter((m) =>
          matchesModelSearch(m, playgroundModelSearch),
        )
      : playgroundModelCatalog;
    return groupModelsByFamily(filtered);
  }, [playgroundModelCatalog, playgroundModelSearch]);

  const playgroundModelTriggerLabel = useMemo(() => {
    const defaultModel = agent?.llmModel || agent?.config?.model || '—';
    if (!modelOverride) return `Default (${defaultModel})`;
    if (modelOverride === 'auto') return 'Auto';
    const known = playgroundModelCatalog.find(
      (m) => m.productId === modelOverride,
    );
    if (known) return formatModelOptionLabel(known);
    return modelOverride;
  }, [
    agent?.llmModel,
    agent?.config?.model,
    modelOverride,
    playgroundModelCatalog,
  ]);

  const closeModelPicker = () => {
    setModelPickerOpen(false);
    setPlaygroundModelSearch('');
  };

  const selectPlaygroundModel = (value: string) => {
    setModelOverride(value);
    closeModelPicker();
  };

  useEffect(() => {
    if (agent?.config) {
      setConfig({
        systemPrompt: agent.config.systemPrompt || '',
        model: agent.config.model || 'gpt-4o',
        temperature: agent.config.temperature ?? 0.7,
      });
    }
  }, [agent?.id, agent?.config]);

  useEffect(() => {
    if (activeTab !== 'playground' || !sessionId || !agent) return;
    subscribe(sessionId, agent.agentName);
    return () => {
      unsubscribe();
    };
  }, [activeTab, sessionId, agent?.agentName, subscribe, unsubscribe]);

  useEffect(() => {
    if (activeTab !== 'playground') return;
    const listener = (event: ExchangeEvent) => {
      setWsEvents((prev) => [event, ...prev].slice(0, 40));
      if (
        event.type === 'exchange.step.completed' ||
        event.type === 'exchange.thinking' ||
        event.type === 'exchange.subagent.started' ||
        event.type === 'exchange.subagent.completed'
      ) {
        const label =
          event.data?.step ||
          event.data?.model ||
          event.data?.agent_name ||
          event.type;
        setLlmLog((prev) => [`${event.type}: ${String(label)}`, ...prev].slice(0, 30));
        if (event.data?.resolved_model) {
          setResolvedModel(String(event.data.resolved_model));
        }
        if (event.data?.resolved_effort) {
          setResolvedEffort(String(event.data.resolved_effort));
        }
        const versionLabel =
          event.data?.resolved_agent_version ||
          event.data?.agent_version ||
          event.data?.['agent-version'];
        if (versionLabel) {
          setResolvedVersion(String(versionLabel));
        }
      }
      if (
        (event.type === 'exchange.response.completed' ||
          event.type === 'conversation.result' ||
          event.type === 'exchange.completed') &&
        event.data?.response
      ) {
        const content = String(event.data.response);
        setAwaitingReply(false);
        setMessages((prev) => {
          if (prev.some((m) => m.content === content && m.role === 'assistant')) {
            return prev;
          }
          return [
            ...prev,
            {
              id: event.exchange_id || `ws-${Date.now()}`,
              role: 'assistant',
              content,
            },
          ];
        });
        return;
      }
      if (
        event.type === 'conversation.result' &&
        (event.data?.status === 'failed' || event.data?.error)
      ) {
        const errText = String(
          event.data?.error || event.data?.message || 'Agent processing failed.',
        );
        setAwaitingReply(false);
        setMessages((prev) => [
          ...prev,
          {
            id: event.exchange_id || `ws-err-${Date.now()}`,
            role: 'assistant',
            content: errText,
          },
        ]);
      }
    };
    addListener(listener);
    return () => removeListener(listener);
  }, [activeTab, addListener, removeListener]);

  useEffect(() => {
    if (agent && !deepLinkSessionId) {
      setMessages([
        {
          id: 'greeting',
          role: 'assistant',
          content: isManagementAgent(agent)
            ? `Hello! I'm ${agent.name}. Upload a document or ask a follow-up — I'll fan out to specialists and return structured results.`
            : `Hello! I'm ${agent.name}. How can I help you today?`,
        },
      ]);
      setSessionId(undefined);
      setHydratedSessionId(undefined);
      setWsEvents([]);
      setLlmLog([]);
    }
  }, [agent?.id, agent?.name, deepLinkSessionId]);

  useEffect(() => {
    if (deepLinkSessionId) {
      setSessionId(deepLinkSessionId);
      setActiveTab('playground');
    }
  }, [deepLinkSessionId]);

  useEffect(() => {
    const targetId = deepLinkSessionId || sessionId;
    if (
      playgroundHistory &&
      targetId &&
      playgroundHistory.sessionId === targetId &&
      hydratedSessionId !== targetId
    ) {
      const hydrated: ChatMessage[] = playgroundHistory.messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m, idx) => ({
          id: `hist-${idx}`,
          role: m.role as 'user' | 'assistant',
          content: m.content,
        }));
      setMessages(
        hydrated.length
          ? hydrated
          : [
              {
                id: 'greeting',
                role: 'assistant',
                content: `Continuing session ${targetId}. How can I help?`,
              },
            ],
      );
      setHydratedSessionId(targetId);
    }
  }, [playgroundHistory, deepLinkSessionId, sessionId, hydratedSessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages]);

  const handleTabChange = (tab: string, extra?: Record<string, string>) => {
    const next = tab as DetailTab;
    setActiveTab(next);
    if (next === 'overview') {
      setSearchParams({});
      return;
    }
    setSearchParams({ tab: next, ...extra });
  };

  const openPlaygroundWithSession = (sid: string) => {
    setSessionId(sid);
    setHydratedSessionId(undefined);
    handleTabChange('playground', { sessionId: sid });
  };

  const startNewPlaygroundSession = () => {
    setSessionId(undefined);
    setHydratedSessionId(undefined);
    setWsEvents([]);
    setLlmLog([]);
    setAwaitingReply(false);
    setResolvedModel(null);
    setResolvedEffort(null);
    setResolvedVersion(null);
    if (agent) {
      setMessages([
        {
          id: 'greeting',
          role: 'assistant',
          content: isManagementAgent(agent)
            ? `Hello! I'm ${agent.name}. Upload a document or ask a follow-up — I'll fan out to specialists and return structured results.`
            : `Hello! I'm ${agent.name}. How can I help you today?`,
        },
      ]);
    }
    handleTabChange('playground');
  };

  const handleOpenCreateVersion = () => {
    if (!agent || readOnly) return;
    setNewVersion(suggestNextVersion(agent));
    setNewVersionNote('');
    setActionError(null);
    setCreateVersionOpen(true);
  };

  const handleCloseCreateVersion = () => {
    setCreateVersionOpen(false);
    setNewVersion('');
    setNewVersionNote('');
  };

  const handleCreateVersion = async () => {
    if (!agent || readOnly) return;
    const version = newVersion.trim();
    if (!version) {
      setActionError('Enter a version number.');
      return;
    }
    setSaving(true);
    setActionError(null);
    try {
      await createAgentVersion({
        id: agent.id,
        version,
        ...(agent.publishedVersion
          ? { source_version: agent.publishedVersion }
          : {}),
        ...(newVersionNote.trim()
          ? { description: newVersionNote.trim() }
          : {}),
      }).unwrap();
      handleCloseCreateVersion();
      handleTabChange('configure');
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to create agent version',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleOpenPublishConfirm = () => {
    if (!agent || readOnly || !canPublishAgent(agent)) return;
    setPublishConfirmOpen(true);
  };

  const handlePublish = async () => {
    if (!agent || readOnly || !canPublishAgent(agent)) return;
    setSaving(true);
    setActionError(null);
    try {
      await publishAgent(agent.id).unwrap();
      setPublishConfirmOpen(false);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to publish agent',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim() || !agent || showPending) return;

    const userMsg: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: chatInput.trim(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setChatInput('');
    setActionError(null);
    setAwaitingReply(true);

    try {
      const agentVersion = playgroundAgentVersion(agent);
      const result = await sendMessage({
        query: userMsg.content,
        agentName: agent.agentName,
        sessionId,
        ...(agentVersion ? { agentVersion } : {}),
        ...(modelOverride ? { model: modelOverride } : {}),
        ...(effortOverride ? { effort: effortOverride } : {}),
      }).unwrap();
      if (result.sessionId) {
        setSessionId(result.sessionId);
        subscribe(result.sessionId, agent.agentName);
      }
      const replyText = (result.response || '').trim();
      const pendingAsync =
        result.isAsync === true ||
        result.status === 'processing' ||
        (!replyText && Boolean(result.conversationId));
      if (pendingAsync) {
        // Keep awaitingReply true until WebSocket delivers the final answer.
        return;
      }
      setAwaitingReply(false);
      if (!replyText) {
        return;
      }
      setMessages((prev) => [
        ...prev,
        {
          id: result.exchangeId || `msg-${Date.now() + 1}`,
          role: 'assistant',
          content: replyText,
        },
      ]);
    } catch (err: unknown) {
      setAwaitingReply(false);
      const status =
        err && typeof err === 'object' && 'status' in err
          ? Number((err as { status?: number }).status)
          : undefined;
      const apiDetail = extractPortalChatError(err);
      const message =
        status === 404
          ? 'Agent not found or you do not have access to invoke it.'
          : status === 401 || status === 403
            ? 'Portal authentication failed for playground chat.'
            : apiDetail ||
              'Unable to reach the portal chat API. Ensure the agent is available for sandbox testing.';
      setActionError(message);
      setMessages((prev) => [
        ...prev,
        {
          id: `msg-${Date.now() + 1}`,
          role: 'assistant',
          content: message,
        },
      ]);
    }
  };

  const stats: StatItem[] = useMemo(() => {
    if (!agent) return [];
    return [
      {
        label: 'Total Sessions',
        value: String(agent.stats.totalSessions.toLocaleString()),
      },
      {
        label: 'Exchanges',
        value: String(agent.stats.totalExchanges.toLocaleString()),
      },
      {
        label: 'Active Applications',
        value: String(agent.stats.activeApplications),
      },
      {
        label: 'Avg Response',
        value:
          agent.stats.avgResponseTime > 0
            ? `${agent.stats.avgResponseTime}s`
            : '—',
      },
    ];
  }, [agent]);

  const timelineVersions: VersionEntry[] = useMemo(() => {
    if (!agent) return [];
    return agent.versions.map((v) => ({
      version: v.version,
      status: v.status,
      date: v.date,
      description: v.description,
      changeCount: v.changeCount,
    }));
  }, [agent]);

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (!id || id === 'new') {
    return (
      <Page>
        <BackLink onClick={() => navigate('/agents')}>
          &larr; Back to Agents
        </BackLink>
        <PageMessage>
          Use Create Agent on the Agents page to start a new draft.
        </PageMessage>
      </Page>
    );
  }

  if (isLoading) {
    return (
      <Page>
        <PageMessage>Loading agent…</PageMessage>
      </Page>
    );
  }

  if (isError || !agent) {
    return (
      <Page>
        <BackLink onClick={() => navigate('/agents')}>
          &larr; Back to Agents
        </BackLink>
        <PageMessage>Agent not found.</PageMessage>
      </Page>
    );
  }

  const scopeCfg = SCOPE_BADGES[agent.scope] ?? SCOPE_BADGES.organisation;
  const management = isManagementAgent(agent);
  const openAgent = (idOrName: string) => navigate(`/agents/${idOrName}`);

  return (
    <Page>
      <AgentHeader>
        <BackLink onClick={() => navigate('/agents')}>
          &larr; Back to Agents
        </BackLink>
        <HeaderTop>
          <HeaderInfo>
            <HeaderRow>
              <Text variant="h3">{agent.name}</Text>
            </HeaderRow>
            {agent.description && (
              <Text
                variant="body1"
                style={{ color: 'var(--text-secondary)', margin: 0 }}
              >
                {agent.description}
              </Text>
            )}
            {management && (
              <SystemLine>
                {agent.fleet?.orchestrationLabel
                  ? `${agent.fleet.orchestrationLabel}. Use Playground or associate this agent with your application.`
                  : 'Management system — use Playground or associate this agent with your application.'}
              </SystemLine>
            )}
            {agent.membership && !management && (
              <SystemLine>
                For production integrations, associate the parent system (
                {agent.membership.parentAgentName}).
              </SystemLine>
            )}
            <BadgeRow>
              <Badge variant={scopeCfg.variant} size="sm">
                {scopeCfg.label}
              </Badge>
              <Badge
                variant={
                  typeBadgeLabel(agent.agentType) === 'Management'
                    ? 'warning'
                    : 'info'
                }
                size="sm"
              >
                {typeBadgeLabel(agent.agentType)}
              </Badge>
              <Badge
                variant={
                  agent.versionStatus === 'published' && !agent.hasOpenDraft
                    ? 'success'
                    : agent.versionStatus === 'sandbox'
                      ? 'warning'
                      : agent.versionStatus === 'draft' || agent.hasOpenDraft
                        ? 'warning'
                        : 'neutral'
                }
                size="sm"
              >
                ● {versionStatusBadgeText(agent)}
              </Badge>
              {(agent.llmModel || agent.config?.model) && (
                <Badge variant="neutral" size="sm">
                  {agent.llmModel || agent.config?.model}
                </Badge>
              )}
              {(() => {
                const recModels =
                  agent.recommendedModels ||
                  agent.config?.recommendedModels ||
                  [];
                return recModels.length > 0 ? (
                  <Badge variant="info" size="sm">
                    {recModels.length} recommended
                  </Badge>
                ) : null;
              })()}
              {(() => {
                const rawEffort =
                  (agent.config as Record<string, unknown> | undefined)
                    ?.effort ??
                  (agent as unknown as Record<string, unknown>).effort;
                const effort =
                  rawEffort === 'low' || rawEffort === 'high'
                    ? rawEffort
                    : 'medium';
                return effort !== 'medium' ? (
                  <Badge variant="warning" size="sm">
                    Effort: {effort}
                  </Badge>
                ) : null;
              })()}
              {readOnly && (
                <Badge variant="neutral" size="sm">
                  Read-only
                </Badge>
              )}
              {agent.membership && (
                <MembershipChip
                  membership={agent.membership}
                  onOpenParent={openAgent}
                />
              )}
            </BadgeRow>
            <HintChips
              agent={agent}
              forceNextSteps={searchParams.get('created') === '1'}
              onOpenPlayground={() => handleTabChange('playground')}
              onAssociate={() => navigate('/applications')}
              onConfigure={() => handleTabChange('configure')}
            />
          </HeaderInfo>
          <HeaderActions>
            <Button
              variant="outline"
              onClick={handleOpenCreateVersion}
              disabled={readOnly || saving}
              title={
                readOnly
                  ? 'Create New Version is not available for read-only agents'
                  : 'Create a draft from the latest published version'
              }
            >
              Create New Version
            </Button>
            <Button
              variant="primary"
              onClick={handleOpenPublishConfirm}
              disabled={readOnly || saving || !canPublishAgent(agent)}
              title={
                canPublishAgent(agent)
                  ? `Publish ${formatVersionLabel(agent.draftVersion || agent.sandboxVersion || agent.version) || agent.version} as latest`
                  : 'Create a new version before publishing'
              }
            >
              Publish
            </Button>
          </HeaderActions>
        </HeaderTop>
      </AgentHeader>

      {actionError && <ErrorBanner>{actionError}</ErrorBanner>}

      <Tabs
        items={TABS}
        activeTab={activeTab}
        onChange={handleTabChange}
        variant="underline"
      />

      <TabContent>
        {activeTab === 'overview' && (
          <OverviewPanels>
            <OverviewPanel>
              <div>
                <StatsGrid items={stats} columns={2} />
              </div>
              <MetaSection>
                <SectionLabel as="h4">Agent Details</SectionLabel>
                <MetaList>
                  <MetaItem>
                    <MetaKey>Type</MetaKey>
                    <MetaValue>{typeBadgeLabel(agent.agentType)}</MetaValue>
                  </MetaItem>
                  {management && (
                    <MetaItem>
                      <MetaKey>Orchestration</MetaKey>
                      <MetaValue>
                        {agent.fleet?.orchestrationLabel ||
                          agent.fleet?.orchestrationMode ||
                          '—'}
                      </MetaValue>
                    </MetaItem>
                  )}
                  <MetaItem>
                    <MetaKey>Default model</MetaKey>
                    <MetaValue>
                      {agent.llmModel || agent.config?.model || '—'}
                    </MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaKey>Effort</MetaKey>
                    <MetaValue>
                      {(() => {
                        const rawEffort =
                          (agent.config as Record<string, unknown> | undefined)
                            ?.effort ??
                          (agent as unknown as Record<string, unknown>).effort;
                        return rawEffort === 'low' || rawEffort === 'high'
                          ? rawEffort
                          : 'medium';
                      })()}
                    </MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaKey>Created</MetaKey>
                    <MetaValue>{formatDate(agent.createdAt)}</MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaKey>Last Updated</MetaKey>
                    <MetaValue>{formatDate(agent.updatedAt)}</MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaKey>Scope</MetaKey>
                    <MetaValue>{scopeCfg.label}</MetaValue>
                  </MetaItem>
                </MetaList>
                {((agent.recommendedModels &&
                  agent.recommendedModels.length > 0) ||
                  (agent.config?.recommendedModels &&
                    agent.config.recommendedModels.length > 0)) && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: 'var(--text-muted)',
                        marginBottom: '0.375rem',
                      }}
                    >
                      Recommended models
                    </div>
                    <MetaChips>
                      {(
                        agent.recommendedModels ||
                        agent.config?.recommendedModels ||
                        []
                      ).map((m) => (
                        <Badge key={m} variant="neutral" size="sm">
                          {m}
                        </Badge>
                      ))}
                    </MetaChips>
                    <Text
                      variant="body1"
                      style={{
                        margin: '0.5rem 0 0',
                        fontSize: '0.75rem',
                        color: 'var(--text-muted)',
                      }}
                    >
                      Playground and API may pass <code>model</code> to override
                      the default for one exchange.
                    </Text>
                  </div>
                )}
              </MetaSection>
              {agent.capabilities && agent.capabilities.length > 0 && (
                <MetaSection>
                  <SectionLabel as="h4">Capabilities</SectionLabel>
                  <CapabilitiesRow capabilities={agent.capabilities} />
                  <MetaChips style={{ marginTop: '0.75rem' }}>
                    <SoftChip
                      type="button"
                      onClick={() => handleTabChange('configure')}
                    >
                      Configure models & sources
                    </SoftChip>
                  </MetaChips>
                </MetaSection>
              )}
              {(!agent.capabilities || agent.capabilities.length === 0) && (
                <MetaSection>
                  <MetaChips>
                    <SoftChip
                      type="button"
                      onClick={() => handleTabChange('configure')}
                    >
                      Configure models & sources
                    </SoftChip>
                  </MetaChips>
                </MetaSection>
              )}
            </OverviewPanel>

            <OverviewPanel>
              {management && agent.fleet && (
                <AgentSystemSummary
                  fleet={agent.fleet}
                  onOpenAgent={openAgent}
                  managerLabel={agent.name}
                />
              )}
              {agent.membership && !management && (
                <PartOfSystemCard
                  membership={agent.membership}
                  onOpenParent={openAgent}
                />
              )}
              <MetaSection>
                <SectionLabel as="h4">Version History</SectionLabel>
                {timelineVersions.length > 0 ? (
                  <VersionTimeline versions={timelineVersions} />
                ) : (
                  <Text variant="body1" style={{ color: 'var(--text-muted)' }}>
                    No version history yet.
                  </Text>
                )}
              </MetaSection>
              <MetaSection>
                <SectionLabel as="h4">Application Associations</SectionLabel>
                {agent.applications.length > 0 ? (
                  <div>
                    {agent.applications.map((app) => (
                      <PartnerRow key={app.id}>
                        <span style={{ fontWeight: 500 }}>{app.name}</span>
                        <Badge
                          variant={
                            String(app.status).toLowerCase() === 'active'
                              ? 'success'
                              : 'neutral'
                          }
                          size="sm"
                        >
                          {app.status}
                        </Badge>
                      </PartnerRow>
                    ))}
                  </div>
                ) : (
                  <Text variant="body1" style={{ color: 'var(--text-muted)' }}>
                    {management
                      ? 'No applications associated. Prefer associating this management agent for production.'
                      : agent.membership
                        ? 'No direct app associations. Prefer associating the parent system for production.'
                        : 'No applications associated.'}
                  </Text>
                )}
              </MetaSection>
              <MetaSection>
                <SectionLabel as="h4">Data sources</SectionLabel>
                {agent.dataSources && agent.dataSources.length > 0 ? (
                  <div>
                    {agent.dataSources.map((ds) => (
                      <PartnerRow key={ds.dataSourceId}>
                        <span style={{ fontWeight: 500 }}>{ds.name}</span>
                        <Badge variant="info" size="sm">
                          {ds.dsType || ds.status || 'Data source'}
                        </Badge>
                      </PartnerRow>
                    ))}
                  </div>
                ) : (
                  <Text variant="body1" style={{ color: 'var(--text-muted)' }}>
                    No data sources associated.
                  </Text>
                )}
                <MetaChips style={{ marginTop: '0.5rem' }}>
                  <SoftChip
                    type="button"
                    onClick={() => handleTabChange('configure')}
                  >
                    Associate a data source
                  </SoftChip>
                </MetaChips>
              </MetaSection>
            </OverviewPanel>
          </OverviewPanels>
        )}

        {activeTab === 'configure' && (
          <ConfigureTab
            agent={agent}
            catalog={catalogAgents}
            readOnly={readOnly}
            onCreateVersion={handleOpenCreateVersion}
          />
        )}

        {activeTab === 'sessions' && (
          <SessionsTab
            agentId={agent.id}
            agentName={agent.agentName}
            onNewSession={startNewPlaygroundSession}
            onOpenInPlayground={openPlaygroundWithSession}
          />
        )}

        {activeTab === 'analytics' && (
          <ScopedAnalyticsPanel
            scope="agent"
            scopeLabel={agent.agentName}
            agentName={agent.agentName}
            onOpenPlayground={() => handleTabChange('playground')}
          />
        )}

        {activeTab === 'playground' && (
          <PlaygroundLayout>
            <ChatContainer>
              <ChatBanner>
                <StatusDot status="info" size="sm" />
                {agent.versionStatus === 'draft'
                  ? 'Draft agents may not be available for chat until published'
                  : management
                    ? sessionId
                      ? `System sandbox · ${agent.agentName} · session ${sessionId.slice(0, 8)}…`
                      : `System sandbox · ${agent.agentName} (fleet invoke)`
                    : sessionId
                      ? `Sandbox chat · ${agent.agentName} · session ${sessionId.slice(0, 8)}…`
                      : `Sandbox chat · ${agent.agentName}`}
                <ModelPickerTrigger
                  type="button"
                  aria-label="Playground model"
                  aria-haspopup="dialog"
                  aria-expanded={modelPickerOpen}
                  title="Override model for this exchange"
                  onClick={() => setModelPickerOpen(true)}
                >
                  {playgroundModelTriggerLabel}
                </ModelPickerTrigger>
                <ModelSelect
                  aria-label="Effort level"
                  value={effortOverride}
                  onChange={(e) =>
                    setEffortOverride(
                      e.target.value as '' | 'low' | 'medium' | 'high',
                    )
                  }
                  title="Effort level for reasoning depth"
                >
                  <option value="">Effort: Default</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </ModelSelect>
                <Button
                  variant="ghost"
                  size="sm"
                  style={{ marginLeft: 'auto' }}
                  onClick={startNewPlaygroundSession}
                >
                  New session
                </Button>
              </ChatBanner>
              <Modal
                isOpen={modelPickerOpen}
                onClose={closeModelPicker}
                title="Select model"
              >
                <ModelPickerBody>
                  <SearchInput
                    value={playgroundModelSearch}
                    onChange={setPlaygroundModelSearch}
                    placeholder="Search by name, id, lab, or route…"
                    debounceMs={0}
                  />
                  <ModelPickerList role="listbox" aria-label="Playground models">
                    <ModelOptionButton
                      type="button"
                      role="option"
                      aria-selected={!modelOverride}
                      $active={!modelOverride}
                      onClick={() => selectPlaygroundModel('')}
                    >
                      Default ({agent.llmModel || agent.config?.model || '—'})
                    </ModelOptionButton>
                    <ModelOptionButton
                      type="button"
                      role="option"
                      aria-selected={modelOverride === 'auto'}
                      $active={modelOverride === 'auto'}
                      onClick={() => selectPlaygroundModel('auto')}
                    >
                      Auto
                    </ModelOptionButton>
                    {playgroundModelGroups.length === 0 ? (
                      <Text
                        variant="body2"
                        style={{
                          margin: 0,
                          padding: '0.75rem 0.25rem',
                          color: 'var(--text-muted)',
                        }}
                      >
                        No models match your search.
                      </Text>
                    ) : (
                      playgroundModelGroups.map((section) => (
                        <div key={section.family}>
                          <ModelFamilyLabel>{section.label}</ModelFamilyLabel>
                          {section.models.map((m) => (
                            <ModelOptionButton
                              key={m.productId}
                              type="button"
                              role="option"
                              aria-selected={modelOverride === m.productId}
                              $active={modelOverride === m.productId}
                              onClick={() => selectPlaygroundModel(m.productId)}
                            >
                              {formatModelOptionLabel(m)}
                            </ModelOptionButton>
                          ))}
                        </div>
                      ))
                    )}
                  </ModelPickerList>
                </ModelPickerBody>
              </Modal>
              <MessageList>
                {messages.map((msg) => (
                  <MessageBubble key={msg.id} $role={msg.role}>
                    {msg.content}
                  </MessageBubble>
                ))}
                {showPending && (
                  <PendingBubble
                    data-testid="playground-pending"
                    role="status"
                    aria-live="polite"
                  >
                    <PendingDots aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </PendingDots>
                    Waiting for response…
                  </PendingBubble>
                )}
                <div ref={messagesEndRef} />
              </MessageList>
              <ChatInputRow onSubmit={handleSendMessage}>
                <ChatInput
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Type a message..."
                  disabled={showPending}
                />
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={!chatInput.trim() || showPending}
                >
                  Send
                </Button>
              </ChatInputRow>
            </ChatContainer>

            <InspectorPanel>
              <InspectorSection>
                <InspectorTitle>Session info</InspectorTitle>
                <Text variant="body1" style={{ margin: 0, fontSize: '0.8125rem' }}>
                  Session: {sessionId || '—'}
                  <br />
                  Agent: {agent.agentName} ({agent.versionStatus})
                  <br />
                  Model:{' '}
                  {modelOverride
                    ? `${modelOverride} · override`
                    : `${agent.llmModel || agent.config?.model || '—'} · default`}
                  <br />
                  Effort: {effortOverride || 'default (medium)'}
                  <br />
                  Version pin:{' '}
                  {playgroundAgentVersion(agent) || 'published (latest)'}
                  <br />
                  {resolvedVersion && (
                    <>Resolved version: {resolvedVersion}<br /></>
                  )}
                  {resolvedModel && (
                    <>Resolved model: {resolvedModel}<br /></>
                  )}
                  {resolvedEffort && (
                    <>Resolved effort: {resolvedEffort}<br /></>
                  )}
                  Messages: {messages.length}
                  <br />
                  WebSocket: {isConnected ? 'connected' : connectionState}
                </Text>
              </InspectorSection>
              <InspectorSection>
                <InspectorTitle>LLM call log</InspectorTitle>
                <EventLog>
                  {llmLog.length === 0 && <li>No LLM events yet.</li>}
                  {llmLog.map((line, idx) => (
                    <li key={`${line}-${idx}`}>{line}</li>
                  ))}
                </EventLog>
              </InspectorSection>
              <InspectorSection>
                <InspectorTitle>WebSocket events</InspectorTitle>
                <EventLog>
                  {wsEvents.length === 0 && <li>Subscribe after the first message.</li>}
                  {wsEvents.map((ev, idx) => (
                    <li key={`${ev.type}-${idx}`}>
                      {ev.timestamp ? `${ev.timestamp} · ` : ''}
                      {ev.type}
                    </li>
                  ))}
                </EventLog>
              </InspectorSection>
              <InspectorSection>
                <InspectorTitle>System prompt</InspectorTitle>
                <JsonPre style={{ fontSize: '0.75rem', maxHeight: 120, overflow: 'auto' }}>
                  {config.systemPrompt || '—'}
                </JsonPre>
              </InspectorSection>
            </InspectorPanel>
          </PlaygroundLayout>
        )}
      </TabContent>

      <Modal
        isOpen={createVersionOpen}
        onClose={handleCloseCreateVersion}
        title="Create New Version"
      >
        <ModalForm>
          <ModalCopy>
            Clones the latest published config into a Draft. You will edit it on
            Configure with Agent Builder.
          </ModalCopy>
          <Input
            label="Version number"
            value={newVersion}
            onChange={(e) => setNewVersion(e.target.value)}
            placeholder="e.g. 3.1"
          />
          <Input
            label="Note (optional)"
            value={newVersionNote}
            onChange={(e) => setNewVersionNote(e.target.value)}
            placeholder="What changed in this version?"
          />
          <ModalActions>
            <Button
              variant="ghost"
              onClick={handleCloseCreateVersion}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateVersion}
              disabled={!newVersion.trim() || saving}
            >
              {saving ? 'Creating…' : 'Create & configure'}
            </Button>
          </ModalActions>
        </ModalForm>
      </Modal>

      <Modal
        isOpen={publishConfirmOpen}
        onClose={() => setPublishConfirmOpen(false)}
        title="Publish version?"
      >
        <ModalForm>
          <ModalCopy>
            Are you sure you want to publish{' '}
            <strong>
              {formatVersionLabel(
                agent.draftVersion ||
                  agent.sandboxVersion ||
                  agent.version,
              ) || agent.version}
            </strong>
            ? It will become the latest version. API callers who omit{' '}
            <code>agent-version</code> will use this version.
          </ModalCopy>
          <ModalActions>
            <Button
              variant="ghost"
              onClick={() => setPublishConfirmOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handlePublish}
              disabled={saving}
            >
              {saving ? 'Publishing…' : 'Yes, publish'}
            </Button>
          </ModalActions>
        </ModalForm>
      </Modal>
    </Page>
  );
};

export default AgentDetail;
